import { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete } from '@mui/material'

import { InputTextAutocompleteProps } from './InputTextAutocompleteProps'
import { AutocompleteValues, InputModes } from 'models/Form/FieldsModels'
import { firstLetterCapitalized } from 'components/Form/Helpers/Parsers'
import analyticsService from 'service/AnalyticsService/AnalyticsService'
import { AnalyticsActionTypes } from 'service/AnalyticsService/AnalyticsServiceModels'

import './InputTextAutocompleteStyles.scss'

/**
 * Kontrolka z listą rozwijalną z automatycznymi podpowiedziami bazującymi na wpisywanym przez użytkownika tekście.
 */
const InputTextAutocomplete = (props: InputTextAutocompleteProps) => {
   const [isMenuOpen, setIsMenuOpen] = useState(false)
   const hasPageBeenRendered = useRef(false)
   const [autocompleteOptions, setAutocomplateOptions] = useState<string[]>([])
   const [value, setValue] = useState<string>(props.value as string)
   const { field, isFirstFill, handleSetIsFieldFirstFill } = props

   const getAutocompleteOptionsCallback = useCallback((currentInputValue: string) => field.getOptionsFunction(currentInputValue), [])


   /**
    * Funkcja obsługująca zmianę tekstu w kontrolce 
   */
   const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if(field.parser !== undefined){
         setValue(field.parser(e.target.value))
      }
      else {
         setValue(e.target.value)
      }

      setAutocomplateOptions(getAutocompleteOptionsCallback(e.target.value))
      setIsMenuOpen(Boolean(autocompleteOptions.length > 0))
   }

   const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isFirstFill && value.length > 0) { // Jeśli pole jest wypełnione po raz pierwszy oraz posiada jakąkolwiek wartość wysyłamy zdarzenie analityczne do bazy
         analyticsService.sendAnalyticAction(field.label, AnalyticsActionTypes.FieldFilledActionId)
         handleSetIsFieldFirstFill(field.name)
      }
      else if (!isFirstFill) { // Jeśli pole BYŁO JUŻ WYPEŁNIONE (AKTUALIZACJA POLA) - w tym przypadku pole nie musi posiadać wartości - użytkownik mógł zaktualizować na pustą wartość
         analyticsService.sendAnalyticAction(field.label, AnalyticsActionTypes.FieldUpdateActionId)
      }

      const currentInputValue = e.target.value
      props.handleValidateField(field.name, currentInputValue, field.validation)

      props.field.onBlurHandler && props.field.onBlurHandler(currentInputValue)
      setIsMenuOpen(false)
   }

   const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setAutocomplateOptions(getAutocompleteOptionsCallback(e.target.value))

      setIsMenuOpen(Boolean(autocompleteOptions.length > 0))
   }

   /**
    * Funkcja obsługująca wybranie opcji z listy rozwijalnej
   */
   const handleOnValueSelect = (e: React.SyntheticEvent, selectedValue: string | null) => {
      if(selectedValue !== null) {
         const newInputValue = firstLetterCapitalized(selectedValue)
         setValue(newInputValue)
         props.handleSaveInputValueToState(field.name, newInputValue, field.validation)

         analyticsService.sendAnalyticAction(field.label, AnalyticsActionTypes.AutocompleteActionId) // Odkładamy w bazie informację o wybraniu opcji z listy rozwijalnej
      }
      else {
         setValue('')
         props.handleSaveInputValueToState(field.name, '', field.validation)
      }

      setIsMenuOpen(false)
   }
   
   useEffect(() => { //Debounce input
      const timeoutId = setTimeout(() => {
         if(hasPageBeenRendered.current){ //Pierwsze błedy walidacyjne wyświetlamy dopiero gdy użytkownik zacznie uzupełniać kontrolki
            props.handleSaveInputValueToState(field.name, value, field.validation)
            props.handleValidateField(field.name, value, field.validation)
            return
         }

         hasPageBeenRendered.current = true // Pierwszy render strony - nie chcemy od razu wyświetlać wszystkich błędów walidacyjnych
      }, props.debounceTime)

      return () => clearTimeout(timeoutId)

      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value, hasPageBeenRendered]);

   return (
      <Autocomplete
         handleHomeEndKeys
         autoComplete // Podpowiadanie końcówek przy przechodzeniu strzałką przez listę
         freeSolo // Akceptuje wszystko co użytkownik wpisze w kontrolę. Wpisana wartość nie musi być zgodna z opcjami z listy
         noOptionsText='Brak podpowiedzi'
         open={Boolean(isMenuOpen && autocompleteOptions.length > 0)}
         options={autocompleteOptions}
         inputValue={props.value as string ?? ''}
         onChange={(e: React.SyntheticEvent, selectedValue: string | null) => handleOnValueSelect(e, selectedValue)}
         classes={{
            paper:   'input-dropdown-dropdown-list-outer-wrapper', 
            option:  'input-dropdown-dropdown-list-option',
            listbox: 'input-dropdown-dropdown-list-remove-scrollbar',
         }}
         renderInput={(params) => (
            <div className='input-dropdown-dropdown-list-input-wrapper' ref={params.InputProps.ref}>
               <input 
                  {...params.inputProps} // Atrybuty kontrolki z biblioteki Mui - muszą być jako pierwsze żeby nie nadpisywały pozostałych wartości
                  id={field.name}
                  name={field.name}
                  autoComplete={field.browserAutocomplete ?? AutocompleteValues.On}
                  inputMode={field.inputMode ?? InputModes.text}
                  value={value}
                  maxLength={!field.mask ? field.maxLength : undefined}
                  placeholder={field.placeholder}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => handleOnFocus(e)}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleOnBlur(e)}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => props.handlePasteDisabled(e, field.isPasteDisabled)}
               />
            </div>
         )}
      />
   )
}

export default InputTextAutocomplete